import { useCallback } from 'react';

import { AxiosError, AxiosResponse } from 'axios';
import { useMutation, UseMutationResult, useQueryClient } from 'react-query';
import { ErrorMessage, Gym, GymRetrieve, GymUpdateOnboardingStep } from 'types';
import { client } from 'utils/api-client';
import notificationHandler from 'utils/notifications';

interface ToggleContext {
  previous?: { data: GymRetrieve };
}

/**
 * Turns a snake_case step key into the camelized field the gym response carries it as.
 */
const toChecklistField = (stepKey: string): string =>
  stepKey.replace(/_([a-z])/g, (_match, letter: string) => letter.toUpperCase());

/**
 * Toggles one onboarding step.
 *
 * Optimistic, because a checkbox that waits on a round trip before it moves reads as broken.
 * The cached gym is snapshotted before the write and restored on error, so a failed save puts
 * the tick back. The cache is invalidated rather than removed.
 */
export default function useUpdateOnboardingStep(
  gymId?: string | null,
): UseMutationResult<AxiosResponse<Gym>, AxiosError<ErrorMessage>, GymUpdateOnboardingStep> {
  const queryClient = useQueryClient();
  const { errorNotification } = notificationHandler();
  const queryKey = ['gyms', gymId];

  const updateStep = useCallback(
    (payload: GymUpdateOnboardingStep): Promise<AxiosResponse<Gym>> => {
      return client.put(`gyms/${gymId}/onboarding-step/`, payload);
    },
    [gymId],
  );

  return useMutation<AxiosResponse<Gym>, AxiosError<ErrorMessage>, GymUpdateOnboardingStep, ToggleContext>(
    updateStep,
    {
      onMutate: async ({ stepKey, checked }) => {
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<{ data: GymRetrieve }>(queryKey);

        if (previous) {
          queryClient.setQueryData<{ data: GymRetrieve }>(queryKey, {
            ...previous,
            data: {
              ...previous.data,
              onboardingChecklist: {
                ...previous.data.onboardingChecklist,
                [toChecklistField(stepKey)]: checked,
              },
            },
          });
        }

        return { previous };
      },
      onError: (_error, _payload, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
        errorNotification('Unable to save your progress. Please try again.');
      },
      onSuccess: () => {
        queryClient.invalidateQueries('gyms');
      },
    },
  );
}
